import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Check, ShoppingCart, Flame } from 'lucide-react';
import Button from '../components/Button';
import QuantitySelector from '../components/QuantitySelector';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useProducts } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fetchProductBySlug, products } = useProducts();
  const { addItem } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);
      const result = await fetchProductBySlug(slug);
      if (result.success) {
        setProduct(result.data.product || result.data);
      } else {
        navigate('/productos');
      }
      setLoading(false);
    };

    loadProduct();
    window.scrollTo(0, 0);
  }, [slug, fetchProductBySlug, navigate]);

  const handleAddToCart = () => {
    if (product && product.stock > 0) {
      addItem(product, quantity);
      setAddedToCart(true);
      // Redirigir al carrito inmediatamente
      navigate('/carrito');
    }
  };

  // Productos relacionados (misma categoría, excluyendo el actual)
  const relatedProducts = products
    .filter(p => p.category === product?.category && p.id !== product?.id && p.stock > 0)
    .slice(0, 4);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!product) return null;

  // Usar current_price del backend si está disponible
  const finalPrice = product.current_price || (product.offer_price && product.offer_price < product.price ? product.offer_price : product.price);
  const hasOffer = product.is_on_sale || (product.offer_price && product.offer_price < product.price);
  const discount = hasOffer ? Math.round((1 - product.offer_price / product.price) * 100) : 0;
  const isOutOfStock = product.stock <= 0;

  return (
    <div className="min-h-screen bg-background pt-16 pb-24">
      {/* Back Button */}
      <div className="px-4 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#7A756F] hover:text-[#1B263B] transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Volver</span>
        </button>
      </div>

      {/* Product Image */}
      <div className="relative aspect-square bg-[#F8F7F5] max-h-[400px]">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain p-8"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Flame size={80} className="text-[#BCB8B1]" />
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {product.featured && (
            <span className="px-3 py-1 bg-primary text-white text-sm font-bold rounded-lg">
              Destacado
            </span>
          )}
          {hasOffer && (
            <span className="px-3 py-1 bg-[#38B000] text-white text-sm font-bold rounded-lg">
              -{discount}% OFF
            </span>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* Category */}
          {product.category && (
            <Link
              to={`/productos?categoria=${product.category}`}
              className="inline-block text-sm text-primary font-semibold mb-2"
            >
              {product.category}
            </Link>
          )}

          {/* Name */}
          <h1 className="text-3xl font-display font-bold text-[#1B263B] mb-2">
            {product.name}
          </h1>

          {/* Weight */}
          {product.weight && (
            <p className="text-lg text-[#7A756F] mb-4">
              {product.weight}
            </p>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-6">
            {hasOffer && (
              <span className="text-2xl text-[#9A9590] line-through">
                ${product.price.toLocaleString()}
              </span>
            )}
            <span className={`text-4xl font-display font-bold ${hasOffer ? 'text-success' : 'text-primary'}`}>
              ${finalPrice.toLocaleString()}
            </span>
          </div>

          {/* Stock status */}
          <div className="flex items-center gap-2 mb-6">
            {isOutOfStock ? (
              <span className="flex items-center gap-1 text-[#DC2626] font-medium">
                <Package size={18} />
                Agotado
              </span>
            ) : product.stock <= 5 ? (
              <span className="flex items-center gap-1 text-warning font-medium">
                <Package size={18} />
                ¡Solo quedan {product.stock} unidades!
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[#38B000] font-medium">
                <Check size={18} />
                Disponible
              </span>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="mb-6">
              <h2 className="font-display font-bold text-lg text-[#1B263B] mb-2">
                Descripción
              </h2>
              <p className="text-[#5A564F] leading-relaxed">
                {product.description}
              </p>
            </div>
          )}

          {/* Add to Cart */}
          {!isOutOfStock && (
            <div className="bg-white p-4 rounded-2xl shadow-lg sticky bottom-24">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium text-[#7A756F]">Cantidad:</span>
                <QuantitySelector
                  value={quantity}
                  onChange={setQuantity}
                  min={1}
                  max={product.stock}
                />
              </div>
              
              <Button
                size="lg"
                fullWidth
                onClick={handleAddToCart}
                disabled={addedToCart}
                className={addedToCart ? 'bg-[#38B000]' : ''}
              >
                {addedToCart ? (
                  <>
                    <Check size={20} />
                    Agregado al carrito
                  </>
                ) : (
                  <>
                    <ShoppingCart size={20} />
                    Agregar al carrito
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Out of Stock message */}
          {isOutOfStock && (
            <div className="bg-[#F8F7F5] p-4 rounded-xl text-center">
              <p className="text-[#7A756F] font-medium">
                Producto temporalmente agotado
              </p>
              <Link to="/productos">
                <Button variant="outline" className="mt-4">
                  Ver otros productos
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="px-4 py-8 border-t border-[#EFEDEA]">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-display font-bold text-[#1B263B] mb-4">
              Productos relacionados
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  onAddToCart={addItem}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
